import type { DeepReadonly, Ref } from 'vue'
import { computed, onUnmounted, readonly, ref } from 'vue'
import type { Router } from 'vue-router'

import { routes } from '@/router'
import { basicRouteGuard } from '@/router/guards'

import type { HotkeyBinding, HotkeyKey } from './hotkeys.types'
import { HotkeyService } from './hotkeyService'
import { useHotkeys } from './useHotkeys'

interface RouteRecord {
  path: string
  name?: string
  hidden?: boolean
  sidebarElements?: boolean
  meta?: { title?: string; icon?: string; roles?: string[]; isEnabled?: () => boolean }
  children?: RouteRecord[]
}

export interface UseMenuHotkeysOptions {
  router: Router
  openSidebar: () => void
}

export interface UseMenuHotkeysReturn {
  isMenuNavActive: DeepReadonly<Ref<boolean>>
  expandedSubMenuIndex: DeepReadonly<Ref<number | null>>
  getLetterForIndex: (index: number) => string
  deactivate: () => void
}

/**
 * Converts a zero-based index to a lowercase letter (0 -> 'a', 1 -> 'b', etc.)
 */
export function getLetterForIndex(index: number): string {
  return String.fromCharCode(97 + index)
}

const MENU_NAV_CONTEXT = 'menu-nav-letters'

export function useMenuHotkeys(options: UseMenuHotkeysOptions): UseMenuHotkeysReturn {
  const { router, openSidebar } = options
  const service = HotkeyService.getInstance()

  const _isMenuNavActive = ref(false)
  const _expandedSubMenuIndex = ref<number | null>(null)

  // Compute visible routes from the sidebar layout route
  const visibleRoutes = computed(() => {
    const layoutRoute = (routes as RouteRecord[]).find(r => r.sidebarElements)
    if (!layoutRoute?.children) {
      return [] as RouteRecord[]
    }
    return layoutRoute.children.filter(route => basicRouteGuard(route))
  })

  function hasModalOverlay(): boolean {
    return !!document.querySelector('.el-dialog, .el-message-box')
  }

  function activate(): void {
    if (hasModalOverlay()) {
      return
    }

    _isMenuNavActive.value = true
    _expandedSubMenuIndex.value = null
    openSidebar()
    registerMenuNavLetters()
  }

  function deactivate(): void {
    _isMenuNavActive.value = false
    _expandedSubMenuIndex.value = null
    unregisterMenuNavLetters()
  }

  function toggleMenuNav(): void {
    if (_isMenuNavActive.value) {
      deactivate()
    } else {
      activate()
    }
  }

  function registerMenuNavLetters(): void {
    const bindings: HotkeyBinding[] = []

    // Escape to deactivate or collapse sub-menu
    bindings.push({
      key: 'Escape',
      handler: () => {
        if (_expandedSubMenuIndex.value !== null) {
          _expandedSubMenuIndex.value = null
          // Re-register to show root-level letters
          registerMenuNavLetters()
        } else {
          deactivate()
        }
      },
      description: 'Close menu navigation'
    })

    if (_expandedSubMenuIndex.value !== null) {
      // Show letters for sub-menu children
      const parentRoute = visibleRoutes.value[_expandedSubMenuIndex.value]
      if (parentRoute?.children) {
        const visibleChildren = parentRoute.children.filter(child => basicRouteGuard(child))
        visibleChildren.forEach((child, index) => {
          const letter = getLetterForIndex(index)
          bindings.push({
            key: letter as HotkeyKey,
            handler: () => {
              const parentPath = parentRoute.path.endsWith('/') ? parentRoute.path : parentRoute.path + '/'
              router.push(parentPath + child.path)
              deactivate()
            },
            description: `Navigate to ${child.meta?.title || child.name}`
          })
        })
      }
    } else {
      // Show letters for root-level routes
      visibleRoutes.value.forEach((route, index) => {
        const letter = getLetterForIndex(index)
        bindings.push({
          key: letter as HotkeyKey,
          handler: () => {
            if (route.children && route.children.length > 0) {
              _expandedSubMenuIndex.value = index
              registerMenuNavLetters()
            } else {
              router.push(route.path)
              deactivate()
            }
          },
          description: `Navigate to ${route.meta?.title || route.name}`
        })
      })
    }

    // Remove existing context before re-registering
    service.removeContext(MENU_NAV_CONTEXT)
    service.registerContext(MENU_NAV_CONTEXT, bindings, { priority: 100 })
    service.activateContext(MENU_NAV_CONTEXT)
  }

  function unregisterMenuNavLetters(): void {
    service.removeContext(MENU_NAV_CONTEXT)
  }

  // Use useHotkeys for the static Ctrl+M trigger (lifecycle-managed)
  useHotkeys(
    'menu-nav-toggle',
    {
      m: {
        handler: () => toggleMenuNav(),
        modifiers: { ctrl: true },
        description: 'Toggle menu navigation'
      }
    },
    { priority: 50 }
  )

  // Clean up the dynamic menu-nav-letters context on unmount
  onUnmounted(() => {
    unregisterMenuNavLetters()
  })

  return {
    isMenuNavActive: readonly(_isMenuNavActive),
    expandedSubMenuIndex: readonly(_expandedSubMenuIndex),
    getLetterForIndex,
    deactivate
  }
}
